import AddIcon from '@mui/icons-material/Add';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { LibraryKind, MediaSourceProvider } from '../../api/types';
import { fits, optionName } from './librarySources';

/// Where one library reads from, on one source: the folders typed in, or the accounts' lists
/// ticked. The same fields make a library and change one, so the two cannot drift apart.
export default function LibrarySourceFields({
  provider,
  addresses,
  onChange,
  kind,
}: {
  provider: MediaSourceProvider;
  addresses: string[];
  onChange: (addresses: string[]) => void;
  /// The kind of an existing library. Only options that fit it are offered; a new library takes
  /// its kind from what is ticked instead.
  kind?: LibraryKind;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const manage = provider.configurable && (
    <Box>
      <Button size="small" onClick={() => navigate(`/plugins/${provider.providerId}`)}>
        {t('libraries.source.manage')}
      </Button>
    </Box>
  );

  if (provider.typedAddresses) {
    // Always one field to type into, so an empty list is never a blank space.
    const folders = addresses.length === 0 ? [''] : addresses;

    return (
      <Stack spacing={2}>
        {folders.map((folder, index) => (
          <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <TextField
              fullWidth
              label={t('libraries.source.folder')}
              placeholder="/media/movies"
              helperText={index === 0 ? t('libraries.source.folderHint') : undefined}
              value={folder}
              onChange={(event) =>
                onChange(folders.map((existing, at) => (at === index ? event.target.value : existing)))
              }
              slotProps={{ htmlInput: { dir: 'ltr', spellCheck: false, autoCapitalize: 'off' } }}
            />
            {folders.length > 1 && (
              <IconButton
                aria-label={t('libraries.source.removeFolder')}
                onClick={() => onChange(folders.filter((_, at) => at !== index))}
                sx={{ mt: 1 }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
            )}
          </Stack>
        ))}

        <Box>
          <Button startIcon={<AddIcon />} onClick={() => onChange([...folders, ''])}>
            {t('libraries.source.addFolder')}
          </Button>
        </Box>

        {manage}
      </Stack>
    );
  }

  if (provider.optionsError) {
    return (
      <Stack spacing={2}>
        <Alert severity="error">{t('libraries.source.optionsFailed', { detail: provider.optionsError })}</Alert>
        {manage}
      </Stack>
    );
  }

  // What fits the library's kind, and whatever it already reads from even where that does not -
  // a library made before the rule - so nothing it reads from is hidden from the one editing it.
  const offered = provider.options.filter(
    (option) => !kind || fits(option, kind) || addresses.includes(option.address),
  );

  // What the library already reads from stays on the list even where the source no longer
  // offers it - an account that was removed - so it can be seen, and taken out.
  const gone = addresses.filter((address) => !provider.options.some((option) => option.address === address));

  if (offered.length === 0 && gone.length === 0) {
    return (
      <Alert
        severity="info"
        action={
          provider.configurable ? (
            <Button color="inherit" size="small" onClick={() => navigate(`/plugins/${provider.providerId}`)}>
              {t('libraries.source.setUp')}
            </Button>
          ) : undefined
        }
      >
        {t('libraries.source.nothingSetUp')}
      </Alert>
    );
  }

  const toggle = (address: string) =>
    onChange(addresses.includes(address) ? addresses.filter((a) => a !== address) : [...addresses, address]);

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {t('libraries.source.choose')}
      </Typography>

      <Stack>
        {offered.map((option) => (
          <FormControlLabel
            key={option.address}
            control={<Checkbox checked={addresses.includes(option.address)} onChange={() => toggle(option.address)} />}
            label={optionName(t, provider, option)}
          />
        ))}

        {gone.map((address) => (
          <FormControlLabel
            key={address}
            control={<Checkbox checked onChange={() => toggle(address)} />}
            label={
              <Typography component="span" color="warning.main">
                {t('libraries.source.unavailable')}{' '}
                <Typography component="span" variant="caption" dir="ltr">
                  ({address})
                </Typography>
              </Typography>
            }
          />
        ))}
      </Stack>

      {manage}
    </Stack>
  );
}
